/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { observer } from 'mobx-react';
import React, { ReactElement } from 'react';
import { useViewModel } from '../../../../../hooks/useViewModel';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Grid, GridLegacy } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import CancelIcon from '@mui/icons-material/Cancel';
import { ArrowDropDown, ArrowRight, Done } from '@mui/icons-material';
import { AddModuleDialogViewModel } from './AddModuleDialogViewModel';
import { ModulePackage } from '../../../../../types/course/ModulePackage';
import AddModuleDialogViewStyles from './AddModuleDialogViewStyles';

const AddModuleDialog = observer((): ReactElement => {
	const viewModel: AddModuleDialogViewModel = useViewModel(AddModuleDialogViewModel);
	const { classes } = AddModuleDialogViewStyles();

	return (
		<Dialog
			open={viewModel.isAddModuleDialogOpen()}
			onClose={() => viewModel.closeAddModuleDialog()}
			fullWidth
			maxWidth={'md'}
			aria-labelledby='add-module-title'
			aria-describedby='add-module-description'>
			<DialogTitle id='add-module-title' className={classes.dialogTitle}>
				<FormattedMessage id='modules.add_modules_dialog.add_modules' />
				<CancelIcon
					fontSize={'small'}
					onClick={() => viewModel.closeAddModuleDialog()}
					className={classes.closeButton}
				/>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				{/* Header Row */}
				<Box className={classes.headerRow}>
					<GridLegacy container alignItems={'center'}>
						<GridLegacy item xs={1} />
						<GridLegacy item xs={4} className={classes.headerCell}>
							<FormattedMessage id='modules.add_modules_dialog.module_name' />
						</GridLegacy>
						<GridLegacy item xs={4} className={classes.headerCell}>
							<FormattedMessage id='modules.add_modules_dialog.module_description' />
						</GridLegacy>
						<GridLegacy item xs={3} />
					</GridLegacy>
				</Box>

				{/* Content */}
				{viewModel.isLoading() ? (
					<Box className={classes.loadingContainer}>
						<CircularProgress className={classes.loading} />
					</Box>
				) : (
					<Box className={classes.contentContainer}>
						{viewModel.getAllModulePackages().map((modulePackage: ModulePackage, index: number) => {
							const isExpanded = viewModel.isCurrentModulePackage(modulePackage.id);
							const isAdded = viewModel.isModulePackageInCourse(modulePackage.id);

							return (
								<Box
									key={modulePackage.id}
									className={`${classes.row} ${index % 2 === 0 ? classes.evenRow : classes.oddRow}`}>
									<GridLegacy container alignItems={'center'}>
										<GridLegacy
											item
											xs={9}
											onClick={() =>
												isExpanded
													? viewModel.setOpenModulePackageId(undefined)
													: viewModel.setOpenModulePackageId(modulePackage.id)
											}
											className={classes.content}>
											<GridLegacy container alignItems={'center'}>
												<GridLegacy item xs={1} className={classes.arrowIcon}>
													{isExpanded ? <ArrowDropDown /> : <ArrowRight />}
												</GridLegacy>
												<GridLegacy item xs={5} className={classes.moduleText}>
													{modulePackage.name}
												</GridLegacy>
												<GridLegacy item xs={5} className={classes.moduleText}>
													<Box className={isExpanded ? '' : classes.truncated}>{modulePackage.description}</Box>
												</GridLegacy>
											</GridLegacy>
										</GridLegacy>
										<GridLegacy item xs={3} className={classes.addButton}>
											{isAdded ? (
												<Button variant='outlined' disabled={true} className={classes.addedButton}>
													<Done fontSize='small' />
													<FormattedMessage id='modules.add_modules_dialog.already_added' />
												</Button>
											) : (
												<Button
													variant='outlined'
													onClick={() => viewModel.addModulePackage(modulePackage.id)}
													className={classes.actionButton}>
													<FormattedMessage id='modules.add_modules_dialog.add_to_course' />
												</Button>
											)}
										</GridLegacy>
									</GridLegacy>
								</Box>
							);
						})}
					</Box>
				)}
			</DialogContent>
		</Dialog>
	);
});

export default AddModuleDialog;
